import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
// GLOBALS
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import * as Sentry from '@sentry/browser';
// HOOKS
import { useAuth } from '../../../../contexts/AuthContext';
import { useForm } from '@tanstack/react-form';
// CHART HANDLING
import useChart from '../../../../hooks/useChart';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
// PATIENT DOCUMENT HANDLING
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
// DATA FNS
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import clone from 'just-clone';
// COMPONENTS
import { FieldInfo } from '../../../../components/FieldInfo';
import { FormSelect } from '../../../../components/forms/FormSelect';
import { InfoTooltip } from '../../../../components/InfoTooltip';
import { PoaUpload } from '../../../../components/forms/PoaUpload';
import Checkbox from '../../../../components/forms/Checkbox';
//TYPES
import { PatientDoc, Chart } from '@usacsmideo/types';

export const Route = createFileRoute('/_authenticated/$patientId/onboarding/questionnaire')({
  component: OnboardingQuestionnaire,
});

import { classNames } from '../../../../style/classNames';

function OnboardingQuestionnaire() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';

  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;

  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;

  const { mutate: mutatePatientInfoDoc } = useUpdateChartSection();
  const { mutate: mutateHCAInfoDoc } = useUpdateChartSection();
  const { mutate: mutateOtherDocumentsDoc } = useUpdateChartSection();
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();

  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  // REFS TO PREVENT RACE CONDITIONS DURING CHAINED MUTATIONS
  const isSubmittingRef = React.useRef(false);
  const capturedFormValuesRef = React.useRef<{
    patientInfo: typeof patientInfoForm.state.values;
    hcaInfo: typeof hcaInfoForm.state.values;
    navInfo: typeof navInfoForm.state.values;
    navType: typeof navTypeForm.state.values;
  } | null>(null);

  // const handleNextButtonClick = async () => {
  //   const res = await updateNavObjAndGetNextScreen({
  //     currentPage: pathname,
  //     patientInfo: patientInfo,
  //     chart: chart,
  //   });

  //   if ('incompleteMessage' in res) {
  //     // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
  //     alert(`Incomplete Information: ${res.incompleteMessage}`);
  //     return;
  //   }
  //   if ('errorMessage' in res) {
  //     // FIRESTORE ERROR
  //     alert(`Error: ${res.errorMessage}`);
  //     return;
  //   }

  //   mutate(
  //     {
  //       patientId: patientId,
  //       navObj: res.updatedNavObj,
  //     },
  //     {
  //       onSuccess: () => {
  //         // NAVIGATE TO THE NEXT SCREEN
  //         navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
  //       },
  //       onError: (error) => {
  //         Sentry.captureException(
  //           new Error(
  //             `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
  //           ),
  //         );
  //         console.error('Error updating patient navigation object:', error);
  //         alert(
  //           'There was an error updating your information. Please try again later or contact support if this issue persists.',
  //         );
  //       },
  //     },
  //   );
  // };

  const handleNavToNextScreen = async () => {
    // REFETCH THE PATIENT DOC TO ENSURE WE HAVE THE LATEST DATA
    // queryClient.refetchQueries({
    //   queryKey: ["patientDoc", patientId],
    // });
    const patientInfoClone = clone(patientInfo);
    const capturedNavInfo = capturedFormValuesRef.current?.navInfo;

    // DISABLE/ENABLE APPROPRIATE CARE TEAM SCREENS BASED ON NAV INFO
    // USE CAPTURED VALUES TO PREVENT RACE CONDITIONS
    patientInfoClone.navObj.attorneyInfo.disabled = capturedNavInfo?.hasAttorney === 'unchecked';
    patientInfoClone.navObj.doctorInfo.disabled = capturedNavInfo?.hasDoctor === 'unchecked';
    patientInfoClone.navObj.hospitalInfo.disabled = capturedNavInfo?.hasHospital === 'unchecked';
    patientInfoClone.navObj.files.disabled = capturedNavInfo?.uploadingDocuments === 'no';

    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfoClone,
      chart: chart,
    });
    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert('Incomplete Information: ' + res.incompleteMessage);
      isSubmittingRef.current = false;
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert('Error: ' + res.errorMessage);
      isSubmittingRef.current = false;
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            isSubmittingRef.current = false;
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            isSubmittingRef.current = false;
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }
    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          isSubmittingRef.current = false;
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'Error: ' +
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // PATIENT INFO
  const patientInfoForm = useForm({
    defaultValues: {
      firstName: chart?.PATIENT_INFO_1?.firstName ? chart.PATIENT_INFO_1.firstName : '',
      gender: chart?.PATIENT_INFO_1?.gender || 'select',
      lastName: chart?.PATIENT_INFO_1?.lastName || '',
      DOB: chart?.PATIENT_INFO_1?.DOB || '',
      selfReporting: chart?.PATIENT_INFO_1?.selfReporting === false ? 'false' : 'true',
    },
    onSubmit: () => {
      // SUBMISSION HANDLED OUTSIDE OF FORM
    },
  });
  const handlePatientInfoUpdate = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    mutatePatientInfoDoc(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_1: {
            firstName: captured.patientInfo.firstName,
            gender: captured.patientInfo.gender,
            lastName: captured.patientInfo.lastName,
            DOB: captured.patientInfo.DOB,
            selfReporting: captured.patientInfo.selfReporting === 'true' ? true : false,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          // setPatientInfoUpdated(true);
          handleHCAInfoUpdate();
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HEALTHCARE AGENT INFO
  const hcaInfoForm = useForm({
    defaultValues: {
      ZIP: chart?.HEALTHCARE_AGENT_INFO?.ZIP || '',
      hasAgent:
        chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney === 'yes' ||
        chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney === 'no'
          ? 'yes'
          : 'select',
      agent: chart?.HEALTHCARE_AGENT_INFO?.agent || '',
      agentToPatient: chart?.HEALTHCARE_AGENT_INFO?.agentToPatient || '',
      cellPhone: chart?.HEALTHCARE_AGENT_INFO?.cellPhone || '',
      city: chart?.HEALTHCARE_AGENT_INFO?.city || '',
      country: chart?.HEALTHCARE_AGENT_INFO?.country || '',
      email: chart?.HEALTHCARE_AGENT_INFO?.email || '',
      hasPowerOfAttorney: chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney || '',
      homePhone: chart?.HEALTHCARE_AGENT_INFO?.homePhone || '',
      patientToAgent: chart?.HEALTHCARE_AGENT_INFO?.patientToAgent || '',
      state: chart?.HEALTHCARE_AGENT_INFO?.state || '',
      street: chart?.HEALTHCARE_AGENT_INFO?.street || '',
    },
    onSubmit: () => {
      // SUBMISSION HANDLED OUTSIDE OF FORM
    },
  });
  const handleHCAInfoUpdate = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    mutateHCAInfoDoc(
      {
        patientId: patientId,
        docData: {
          HEALTHCARE_AGENT_INFO: {
            ZIP: captured.hcaInfo.ZIP,
            agent: captured.hcaInfo.agent,
            agentToPatient: captured.hcaInfo.agentToPatient,
            cellPhone: captured.hcaInfo.cellPhone,
            city: captured.hcaInfo.city,
            country: captured.hcaInfo.country,
            email: captured.hcaInfo.email,
            hasPowerOfAttorney: captured.hcaInfo.hasPowerOfAttorney,
            homePhone: captured.hcaInfo.homePhone,
            patientToAgent: captured.hcaInfo.patientToAgent,
            state: captured.hcaInfo.state,
            street: captured.hcaInfo.street,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          // setHCAInfoUpdated(true);
          handlePaymentInfoUpdate();
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // PAYMENT INFO — pass-through: PaymentPrefs is now the sole PAYMENT_INFO writer
  const handlePaymentInfoUpdate = () => {
    handleNavInfoUpdate();
  };

  // NAV INFO
  const createEmptyOtherDocumentsDoc = () => {
    mutateOtherDocumentsDoc(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            POLSTURI: '',
            livingWillURI: '',
            powerOfAttorneyURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // RESET ALL UPDATE FLAGS SO AUTOMATIC NAVIGATION WORKS CORRECTLY IF THE PATIENT RETURNS TO THIS SCREEN
          // setPatientInfoUpdated(false);
          // setHCAInfoUpdated(false);
          // setPaymentInfoUpdated(false);
          // setNavInfoUpdated(false);

          // GO TO FINAL SCREEN
          handleNavToNextScreen();
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };
  const handleNavObjUpdate = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    const patientInfoClone = clone(patientInfo);

    // DISABLE/ENABLE APPROPRIATE CARE TEAM SCREENS BASED ON NAV INFO
    // USE CAPTURED VALUES TO PREVENT RACE CONDITIONS
    // EXPLICITLY SET BOTH true AND false BASED ON VALUE
    patientInfoClone.navObj.attorneyInfo.disabled = captured.navInfo.hasAttorney === 'unchecked';
    patientInfoClone.navObj.doctorInfo.disabled = captured.navInfo.hasDoctor === 'unchecked';
    patientInfoClone.navObj.hospitalInfo.disabled = captured.navInfo.hasHospital === 'unchecked';
    patientInfoClone.navObj.files.disabled = captured.navInfo.uploadingDocuments === 'no';

    mutatePatientDoc(
      {
        patientId,
        navObj: patientInfoClone.navObj,
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          if (captured.navInfo.uploadingDocuments === 'no') {
            // CREATE AN EMPTY OTHER DOCUMENTS DOC SO VIDEO PROCESSING DOESN'T FAIL
            createEmptyOtherDocumentsDoc();
          } else {
            // RESET ALL UPDATE FLAGS SO AUTOMATIC NAVIGATION WORKS CORRECTLY IF THE PATIENT RETURNS TO THIS SCREEN
            // setPatientInfoUpdated(false);
            // setHCAInfoUpdated(false);
            // setPaymentInfoUpdated(false);
            // setNavInfoUpdated(false);

            // GO TO FINAL SCREEN
            handleNavToNextScreen();
          }
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };
  const navInfoForm = useForm({
    defaultValues: {
      uploadingDocuments:
        patientInfo?.navInfo?.uploadingDocuments === undefined
          ? 'select'
          : patientInfo.navInfo.uploadingDocuments === true
            ? 'yes'
            : 'no',
      hasDoctor:
        patientInfo?.navInfo?.hasDoctor === undefined
          ? 'checked' // DEFAULT IS TO SEND COPY TO DOCTOR
          : patientInfo.navInfo.hasDoctor === true
            ? 'checked'
            : 'unchecked',
      hasHospital:
        patientInfo?.navInfo?.hasHospital === undefined
          ? 'checked' // DEFAULT IS TO SEND COPY TO HOSPITAL
          : patientInfo?.navInfo?.hasHospital === true
            ? 'checked'
            : 'unchecked',
      hasAttorney:
        patientInfo?.navInfo?.hasAttorney === undefined
          ? 'checked' // DEFAULT IS TO SEND COPY TO ATTORNEY
          : patientInfo?.navInfo?.hasAttorney === true
            ? 'checked'
            : 'unchecked',
    },
    onSubmit: () => {
      // SUBMISSION HANDLED OUTSIDE OF FORM
    },
  });
  const navTypeForm = useForm({
    defaultValues: {
      base: patientInfo?.navType?.base === undefined ? 'select' : patientInfo.navType?.base,
    },
    onSubmit: () => {
      // SUBMISSION HANDLED OUTSIDE OF FORM
    },
  });
  const handleNavInfoUpdate = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    mutatePatientDoc(
      {
        patientId: patientId,
        navType: {
          ...patientInfo.navType,
          initialPreference: captured.navType?.base as 'self' | 'concierge',
        },
        navInfo: {
          uploadingDocuments: captured.navInfo.uploadingDocuments === 'yes' ? true : false,
          hasDoctor: captured.navInfo.hasDoctor === 'checked' ? true : false,
          hasHospital: captured.navInfo.hasHospital === 'checked' ? true : false,
          hasAttorney: captured.navInfo.hasAttorney === 'checked' ? true : false,
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          handleNavObjUpdate();
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIATE FIRST DOCUMENT UPDATE. THEY WILL THEN BE CHAINED TOGETHER THROUGH THE ON_SUCCESS CALLBACKS
  const handleInfoUpdate = async () => {
    // CAPTURE ALL FORM VALUES AT SUBMIT TIME TO PREVENT RACE CONDITIONS
    // This ensures that if useEffect resets forms mid-mutation, we still have the original values
    isSubmittingRef.current = true;
    capturedFormValuesRef.current = {
      patientInfo: { ...patientInfoForm.state.values },
      hcaInfo: { ...hcaInfoForm.state.values },
      navInfo: { ...navInfoForm.state.values },
      navType: { ...navTypeForm.state.values },
    };
    handlePatientInfoUpdate();
  };

  // RESET FORMS AFTER CHART DATA LOADS (OR CHANGES)
  // SKIP RESET IF WE'RE IN THE MIDDLE OF A MUTATION CHAIN TO PREVENT RACE CONDITIONS
  //
  // Key the deps on ONLY the chart/patient slices these forms actually read — NOT
  // the whole chart object. Otherwise an unrelated chart write (e.g. PoaUpload
  // writing OTHER_DOCUMENTS mid-questionnaire) changes the chart reference, fires
  // this effect, and resets the forms back to their persisted values — wiping the
  // user's in-progress, not-yet-submitted answers (CU-86aha0x95). The forms never
  // read OTHER_DOCUMENTS, so a change there must not trigger a reset.
  const formRelevantSignature = JSON.stringify({
    PATIENT_INFO_1: chart?.PATIENT_INFO_1,
    HEALTHCARE_AGENT_INFO: chart?.HEALTHCARE_AGENT_INFO,
    navObj: patientInfo?.navObj,
    navInfo: patientInfo?.navInfo,
    navType: patientInfo?.navType,
  });
  React.useEffect(() => {
    if (isSubmittingRef.current) {
      // Skip form reset while mutations are in progress
      return;
    }
    if (chart || patientInfo) {
      patientInfoForm.reset();
      hcaInfoForm.reset();
      navInfoForm.reset();
      navTypeForm.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRelevantSignature]);

  return (
    <>
      <div className="flex min-h-screen w-full flex-col items-center py-10">
        <p className="text-mideo-darkred text-3xl font-bold">Patient Onboarding</p>

        {/* SELF REPORTING */}
        <patientInfoForm.Field name="selfReporting">
          {(field) => (
            <div className="w-1/2">
              <FormSelect
                id="select-input"
                label="Are you completing the application for yourself, or are you the legally appointed Healthcare Agent for the patient receiving the MIDEO?"
                name="selfReporting"
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value ?? 'true')}
                options={[
                  { name: 'I am the patient', value: 'true' },
                  { name: "I am the patient's agent", value: 'false' },
                ]}
                required={true}
                value={field.state.value}
              />
              <FieldInfo fieldMeta={field.state.meta} />
            </div>
          )}
        </patientInfoForm.Field>

        {/* HCA INFO */}
        <hcaInfoForm.Field name="hasAgent">
          {(field) => (
            <div className="w-1/2">
              <div className="flex items-center gap-1">
                <FormSelect
                  id="select-input"
                  name="hasAgent"
                  label="Have you already named a Healthcare Agent?"
                  onBlur={field.handleBlur}
                  onChange={(e) => {
                    if (e.target.value === 'no') {
                      confirm(
                        'Agent Required: ' +
                          "You can use the MIDEO process to name a Healthcare Agent in the 'Care Team' section of this app. Please talk with a trusted individual before completing that section. You will be able to continue working in other sections in the meantime.",
                      );
                    }
                    field.handleChange(e.target.value ?? 'yes');
                  }}
                  options={[
                    { name: 'Select an option', value: 'select' },
                    { name: 'Yes', value: 'yes' },
                    { name: 'No', value: 'no' },
                  ]}
                  required={true}
                  value={field.state.value}
                />
                <div className="mt-7">
                  <InfoTooltip
                    label="What is a Healthcare Agent?"
                    content="This is the person who makes decisions regarding your healthcare if you can't speak for yourself. Common examples are a spouse, friend, parent, child, sibling, or other trusted individual."
                  />
                </div>
              </div>
              <FieldInfo fieldMeta={field.state.meta} />
            </div>
          )}
        </hcaInfoForm.Field>

        {/* ONLY SHOW THE POA QUESTION IF THE USER HAS AN AGENT */}
        <hcaInfoForm.Subscribe selector={(state) => state.values.hasAgent}>
          {(hasAgent) => {
            if (hasAgent === 'yes')
              return (
                <hcaInfoForm.Field name="hasPowerOfAttorney">
                  {(field) => (
                    <div className="w-1/2">
                      <div className="flex items-center gap-1">
                        <FormSelect
                          id="select-input"
                          label="Is your Healthcare Agent also your legally appointed Healthcare Power of Attorney?"
                          name="hasPowerOfAttorney"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value ?? 'no')}
                          options={[
                            { name: 'Select an option', value: 'select' },
                            { name: 'Yes', value: 'yes' },
                            {
                              name: 'No',
                              value: 'no',
                            },
                          ]}
                          required={true}
                          value={field.state.value}
                        />
                        <div className="mt-7">
                          <InfoTooltip
                            label="What does legally appointed Power of Attorney mean?"
                            content="Have you appointed this person as your healthcare agent in a legal document such as a living will or healthcare power of attorney form?"
                          />
                        </div>
                      </div>
                      <FieldInfo fieldMeta={field.state.meta} />
                    </div>
                  )}
                </hcaInfoForm.Field>
              );
          }}
        </hcaInfoForm.Subscribe>

        {/* PoA DOCUMENT UPLOAD — shown when the agent IS the legal Power of Attorney.
            PoaUpload (authored by CU-86aha36kx) is self-contained: it owns the
            now/later choice, the OTHER_DOCUMENTS writes, and the render precedence.
            We only decide when to show it (hasPowerOfAttorney === 'yes'). */}
        <hcaInfoForm.Subscribe selector={(state) => state.values.hasPowerOfAttorney}>
          {(hasPowerOfAttorney) =>
            hasPowerOfAttorney === 'yes' ? (
              <div className="w-1/2">
                <PoaUpload patientId={patientId} chart={chart} />
              </div>
            ) : null
          }
        </hcaInfoForm.Subscribe>

        {/* UPLOAD FILES QUESTION */}
        <hcaInfoForm.Subscribe selector={(state) => state.values}>
          {(values) => {
            if (values.hasAgent === 'select') return null;
            return (
              <navInfoForm.Field
                name="uploadingDocuments"
                validators={{
                  onChange: (val) => {
                    if (values.hasAgent === 'yes' && values.hasPowerOfAttorney === 'yes' && val.value === 'no') {
                      confirm(
                        'File Required: ' +
                          'Patients whose HCA is also their Healthcare Power of Attorney must upload a copy of the document. Please update your selection.',
                      );
                    }
                    return values.hasAgent === 'yes' && values.hasPowerOfAttorney === 'yes' && val.value === 'no'
                      ? 'Healthcare Power of Attorney document must be uploaded.'
                      : undefined;
                  },
                }}
              >
                {(field) => (
                  <div className="w-1/2">
                    <FormSelect
                      id="select-input"
                      label="Would you like to upload any existing legal documents (Living Will, POLST, etc.) so they can be read when your MIDEO card is scanned?"
                      name="uploadingDocuments"
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value ?? 'select');
                      }}
                      options={[
                        { name: 'Select an option', value: 'select' },
                        { name: 'Yes', value: 'yes' },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      required={true}
                      value={field.state.value}
                    />
                    <FieldInfo fieldMeta={field.state.meta} />
                  </div>
                )}
              </navInfoForm.Field>
            );
          }}
        </hcaInfoForm.Subscribe>

        {/* CARE TEAM CHECKBOX GROUP */}
        <navInfoForm.Subscribe selector={(state) => state.values}>
          {(values) => {
            if (values.uploadingDocuments === 'select') return null;
            return (
              <div className="flex w-6/12 flex-col items-center py-10">
                <p className="text-xl font-bold">
                  Would you like a copy of your MIDEO sent to your doctor, hospital, and/or attorney?
                </p>
                <navInfoForm.Field name="hasDoctor">
                  {(field) => (
                    <Checkbox
                      id="has-doctor-checkbox"
                      label="Send info to my PCP"
                      name="hasDoctor"
                      checked={field.state.value === 'checked'}
                      onChange={(e) => field.handleChange(e.target.checked ? 'checked' : 'unchecked')}
                    />
                  )}
                </navInfoForm.Field>
                <navInfoForm.Field name="hasHospital">
                  {(field) => (
                    <Checkbox
                      id="has-hospital-checkbox"
                      label="Send info to my preferred hospital"
                      name="hasHospital"
                      checked={field.state.value === 'checked'}
                      onChange={(e) => field.handleChange(e.target.checked ? 'checked' : 'unchecked')}
                    />
                  )}
                </navInfoForm.Field>
                <navInfoForm.Field name="hasAttorney">
                  {(field) => (
                    <Checkbox
                      id="has-attorney-checkbox"
                      label="Send info to my attorney"
                      name="hasAttorney"
                      checked={field.state.value === 'checked'}
                      onChange={(e) => field.handleChange(e.target.checked ? 'checked' : 'unchecked')}
                    />
                  )}
                </navInfoForm.Field>
              </div>
            );
          }}
        </navInfoForm.Subscribe>

        {/* CONCIERGE OR SELF-GUIDED */}
        <hcaInfoForm.Subscribe selector={(state) => state.values}>
          {(values) => {
            if (values.hasAgent === 'select') return null;
            return (
              <>
                <div className="flex w-6/12 flex-col items-center py-10">
                  <p className="text-xl font-bold">
                    There are two paths you can take to complete your MIDEO. Choose the one below that best fits your
                    current situation and health.
                  </p>
                </div>
                <navTypeForm.Field
                  name="base"
                  validators={{
                    onChange: (val) => (val.value === 'select' ? 'Please select an option' : undefined),
                  }}
                >
                  {(field) => {
                    const tiles = [
                      {
                        value: 'self',
                        title: 'Self-Guided',
                        description:
                          'Choose this path if you would like to complete our Self-Guided MIDEO where you can record a full code message that instructs your care team to provide all lifesaving measures in an emergency. This path is recommended for those who are age 18-64 with no serious medical conditions.',
                      },
                      {
                        value: 'concierge',
                        title: 'Physician-Guided',
                        description:
                          'Choose this path if you are 65+, have serious medical condition(s), or if you have strong beliefs regarding end of life care that you would like to discuss with a physician who specializes in advance care planning. This path is also recommended for those looking to use their health insurance or Medicare to cover their MIDEO.',
                      },
                    ];
                    return (
                      <div className="flex w-10/12 flex-col gap-4 lg:flex-row">
                        {tiles.map((tile) => {
                          const isSelected = field.state.value === tile.value;
                          return (
                            <button
                              key={tile.value}
                              type="button"
                              onClick={() => field.handleChange(tile.value)}
                              className={`flex flex-1 flex-col rounded-lg p-6 text-left shadow-lg outline ${
                                isSelected ? 'outline-4 outline-sky-700' : 'outline-1 outline-gray-300'
                              }`}
                            >
                              <div className="flex flex-row items-center justify-between">
                                <h3 className="text-2xl font-bold">{tile.title}</h3>
                                {isSelected && <span className="font-bold text-sky-700">Selected ✓</span>}
                              </div>
                              <p className="mt-2 text-base">{tile.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    );
                  }}
                </navTypeForm.Field>
              </>
            );
          }}
        </hcaInfoForm.Subscribe>

        {/* SUBMIT BUTTON */}
        <hcaInfoForm.Subscribe selector={(state) => state.values}>
          {(hcaValues) => (
            <navInfoForm.Subscribe selector={(state) => state.values.uploadingDocuments}>
              {(uploadingDocuments) => (
                <navTypeForm.Subscribe selector={(state) => state.values.base}>
                  {(base) => {
                    const hasAgentReady = hcaValues.hasAgent !== 'select';
                    const hasPOAReady =
                      hcaValues.hasAgent !== 'yes' ||
                      (hcaValues.hasPowerOfAttorney !== 'select' && hcaValues.hasPowerOfAttorney !== '');
                    const isReady =
                      hasAgentReady &&
                      hasPOAReady &&
                      uploadingDocuments !== 'select' &&
                      base !== 'select';
                    if (!isReady) return null;
                    return (
                      <button
                        id="submit-form-info"
                        title="Submit Form Info"
                        onClick={handleInfoUpdate}
                        disabled={false}
                        className={classNames.nextButton}
                      >
                        Submit and Continue
                      </button>
                    );
                  }}
                </navTypeForm.Subscribe>
              )}
            </navInfoForm.Subscribe>
          )}
        </hcaInfoForm.Subscribe>
      </div>
    </>
  );
}

export default OnboardingQuestionnaire;
